/**
 * 客户端状态机增强
 * 自定义状态 + 转换规则 + 钩子
 */
export interface StateTransition<S extends string = string> {
  from: S | S[]
  to: S
  event: string
  guard?: (context: Record<string, any>) => boolean
  action?: (context: Record<string, any>) => void
}

export interface StateMachineOptions<S extends string = string> {
  initial: S
  transitions: StateTransition<S>[]
  onEnter?: Partial<Record<S, (ctx: Record<string, any>) => void>>
  onExit?: Partial<Record<S, (ctx: Record<string, any>) => void>>
}

export class StateMachine<S extends string = string> {
  private current: S
  private transitions: StateTransition<S>[]
  private context: Record<string, any> = {}
  private onEnter: Partial<Record<S, (ctx: Record<string, any>) => void>>
  private onExit: Partial<Record<S, (ctx: Record<string, any>) => void>>
  private history: Array<{ from: S; to: S; event: string; timestamp: number }> = []

  constructor(options: StateMachineOptions<S>) {
    this.current = options.initial
    this.transitions = options.transitions
    this.onEnter = options.onEnter ?? {}
    this.onExit = options.onExit ?? {}
  }

  /** 触发事件 */
  send(event: string): boolean {
    const transition = this.transitions.find((t) => {
      const fromMatch = Array.isArray(t.from)
        ? t.from.includes(this.current)
        : t.from === this.current
      return fromMatch && t.event === event
    })

    if (!transition) return false
    if (transition.guard && !transition.guard(this.context)) return false

    const from = this.current
    this.onExit[from]?.(this.context)
    transition.action?.(this.context)
    this.current = transition.to
    this.onEnter[transition.to]?.(this.context)
    this.history.push({ from, to: transition.to, event, timestamp: Date.now() })

    return true
  }

  /** 获取当前状态 */
  get state(): S {
    return this.current
  }

  /** 设置上下文 */
  setContext(ctx: Record<string, any>): void {
    Object.assign(this.context, ctx)
  }

  /** 获取上下文 */
  getContext(): Record<string, any> {
    return { ...this.context }
  }

  /** 获取可用事件 */
  getAvailableEvents(): string[] {
    return this.transitions
      .filter((t) => {
        const fromMatch = Array.isArray(t.from)
          ? t.from.includes(this.current)
          : t.from === this.current
        return fromMatch
      })
      .map((t) => t.event)
  }

  /** 是否可以转换 */
  can(event: string): boolean {
    return this.getAvailableEvents().includes(event)
  }

  /** 获取历史 */
  getHistory(): typeof this.history {
    return [...this.history]
  }

  /** 重置到初始状态 */
  reset(initial: S): void {
    this.current = initial
    this.history = []
    this.context = {}
  }
}
